<template>
  <validation-observer v-if="dialogComputed" ref="observer" v-slot="{ invalid }">
    <v-dialog v-model="dialogComputed" max-width="500px" scrollable :persistent="!isInquiry">
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ setting.title }}
          <v-spacer />
          <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text class="px-0">
          <v-container>
            <v-row>
              <v-col>
                <TextFieldRef v-if="isInquiry" :label="$t('ELE0033')" :value="inspTitleComputed" />
                <TextFieldInput v-else :label="$t('ELE0033')" v-model="inspTitleComputed" :maxLength="40" required />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextAreaRef v-if="isInquiry" :label="$t('ELE0034')" :value="inspPointComputed" :rows="2" />
                <TextAreaInput
                  v-else
                  :label="$t('ELE0034')"
                  v-model="inspPointComputed"
                  :rows="2"
                  :maxLength="1000"
                  required
                />
              </v-col>
            </v-row>
            <v-row class="pb-0">
              <v-col>
                <FileInput v-if="isAdd" v-model="file" :label="$t('ELE0035')" required />
                <FileInput v-else-if="isEdit" v-model="file" :label="$t('ELE0035')" />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0">
                <FileView :path="filePath" />
              </v-col>
            </v-row>
          </v-container>
          <v-divider />
        </v-card-text>
        <v-card-actions>
          <BtnDelete v-if="isEdit" @click="confirm" />
          <v-spacer />
          <span class="d-flex flex-row space-between">
            <BtnClose v-if="isInquiry" @click="close" />
            <BtnSave v-else @click="onClickSave" :disabled="invalid" />
          </span>
        </v-card-actions>

        <ConfirmDialog
          :dialog.sync="confirmDialog.flag"
          :confirmWord="confirmDialog.confirmWord"
          :btnLabel="confirmDialog.btnLabel"
          @cancel="cancel"
          @proceed="proceed"
        />
      </v-card>
    </v-dialog>
  </validation-observer>
</template>

<script>
import { API } from '@/plugins/api'
import { ValidationObserver } from 'vee-validate'
import { ModalMode } from '@/enum'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import { Storage } from '@/plugins/storage'
import { S3Root } from '@/enum'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput'
import TextAreaRef from '@/components/atoms/base/text-field/TextAreaRef'
import Btn from '@/components/atoms/base/button/Btn'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnClose from '@/components/atoms/button/BtnClose'
import BtnDelete from '@/components/atoms/button/BtnDelete'
import FileInput from '@/components/atoms/base/file/FileInput'
import ConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'
import FileView from '@/components/organisms/common/preview/FileView'

export default {
  name: 'InspAttachmentDialog',

  mixins: [APIUtils, Message, Loading],

  components: {
    ValidationObserver,
    TextFieldInput,
    TextFieldRef,
    TextAreaInput,
    TextAreaRef,
    Btn,
    FileInput,
    BtnSave,
    BtnDelete,
    BtnClose,
    ConfirmDialog,
    FileView,
  },
  data() {
    return {
      settings: [
        {
          // 追加モード時の処理
          mode: ModalMode.ADD,
          title: this.$t('ELE0016'),
          url: '/master/partInspPoints/register',
        },
        {
          // 編集モード時の処理
          mode: ModalMode.EDIT,
          title: this.$t('ELE0017'),
          url: '/master/partInspPoints/{id}/update',
        },
        {
          // 編集モード時の処理
          mode: ModalMode.INQUIRY,
          title: this.$t('ELE0018'),
          url: '',
        },
      ],
      file: null,
      confirmDialog: {
        flag: false,
        confirmWord: '',
        btnLabel: '',
      },
    }
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    inspTitle: {
      type: String,
      required: true,
    },
    inspPoint: {
      type: String,
      required: true,
    },
    partCd: {
      type: String,
      required: true,
    },
    inspPointNo: {
      type: Number,
      required: true,
    },
    filePath: {
      type: String,
      required: true,
    },
    versionNo: {
      type: Number,
      required: true,
    },
  },

  computed: {
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    inspTitleComputed: {
      get() {
        return this.inspTitle
      },
      set(value) {
        this.$emit('update:inspTitle', value)
      },
    },
    inspPointComputed: {
      get() {
        return this.inspPoint
      },
      set(value) {
        this.$emit('update:inspPoint', value)
      },
    },
    filePathComputed: {
      get() {
        return this.filePath
      },
      set() {},
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    isAdd: {
      get() {
        return this.setting.mode === ModalMode.ADD
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === ModalMode.EDIT
      },
    },
    isInquiry: {
      get() {
        return this.setting.mode === ModalMode.INQUIRY
      },
    },
  },
  methods: {
    close() {
      this.file = null
      this.dialogComputed = false
    },
    cancel() {
      this.confirmDialog.flag = false
    },
    async proceed() {
      this.confirmDialog.flag = false
      this.startLoading()
      try {
        const body = {
          partCd: this.partCd,
          inspPointNo: this.inspPointNo,
        }
        const url = '/master/partInspPoints/' + body.partCd + '/delete'

        await Storage.deleteFile(this.filePath)
        await API.post(url, body)
        this.setSuccessMessage(this.$t('MSG0014'))
        this.cancel()
        this.close()
        this.$emit('change')
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0004', { p0: this.partCd })))
      } finally {
        this.stopLoading()
      }
    },
    confirm() {
      this.confirmDialog.confirmWord = this.$t('MSG0013')
      this.confirmDialog.btnLabel = this.$t('ELE0036')
      this.confirmDialog.flag = true
    },
    async onClickSave() {
      this.startLoading()
      try {
        if (!(await this.$refs.observer.validate())) {
          return
        }

        let path = this.filePathComputed

        if (this.file) {
          if (this.isEdit) {
            await Storage.deleteFile(this.filePath)
          }
          path = await Storage.uploadFile(this.file, S3Root.INSP_POINT + '/' + this.partCd)
        }

        const body = {
          partCd: this.partCd,
          inspPointNo: this.inspPointNo,
          inspTitle: this.inspTitleComputed,
          inspPoint: this.inspPointComputed,
          filePath: path,
          versionNo: this.versionNo,
        }
        const url = this.setting.url.replace('{id}', this.partCd)
        await API.post(url, body)

        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0034') }))
        this.close()
        this.$emit('change')
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0034') })))
      } finally {
        this.stopLoading()
      }
    },
  },
}
</script>
